/* eslint-disable no-nested-ternary */
import React, { useEffect, useMemo } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { Row, Col } from 'react-bootstrap';
import {
  Button,
  Typography,
  Card,
  Table,
  CardContent,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  TableFooter,
  Link,
} from '@mui/material';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useIntl } from 'react-intl';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

import { SHeadTableCell } from '../../../../components/styledComponents/Tables';
import Preloader from '../../../../components/other/Preloader/Preloader';
import { Modal } from '../../../../components/other/Modals';
import TablePaginator from '../../../../components/tableComponents/TablePaginator/TablePaginator';

import { setLayoutFooter, setLayoutSubheader } from '../../../../utils/layout';
import { IAppState } from '../../../../store/rootDuck';
import { actions as usersActions } from '../../../../store/ducks/users.duck';
import { getRole, getRoleBreadcrumbName, getFio } from './utils';
import useCrudSnackbar from '../../../../hooks/useCrudSnackbar';
import { UserListType } from '../constants';
import homeStyles from '../../../../constants/homeStyles';
import { useStyles } from './hooks/useStyles';
import { UserRoles } from '../../../../interfaces/user';
import { useFormatMessage } from '../../../../hooks';

const UsersPage: React.FC<TPropsFromRedux> = ({
  page,
  perPage,
  total,
  userRole,
  users,
  fetch,
  loading,
  clearDelUser,
  delUser,
  delUserSuccess,
  delUserLoading,
  delUserError,
  filter,
  clearAddUserId,
}) => {
  const intl = useIntl();
  const classes = useStyles();
  const homeClasses = homeStyles();
  const navigate = useNavigate();
  const { type } = useParams();
  const fm = useFormatMessage();

  const [open, setOpen] = React.useState(false);
  const [userId, setUserId] = React.useState<number>();
  const role: any = getRole(type as UserListType);

  const isVendorStaff = useMemo(() => role === 'ROLE_VENDOR_STAFF', [role]);
  const isBuyerStaff = useMemo(() => role === 'ROLE_BUYER_STAFF', [role]);
  const isAdmin = useMemo(() => userRole?.[0] === UserRoles.ROLE_ADMIN, [userRole]);

  // For delete actions
  useCrudSnackbar({
    success: delUserSuccess,
    error: delUserError,
    clear: clearDelUser,
    successMessage: fm('USER.SNACKBAR.DELETE.SUCCESS'),
    errorMessage: `${fm('ERROR')}: ${delUserSuccess}`,
    afterSuccessOrError: () => {
      setOpen(false);
      clearDelUser();
    },
    afterSuccess: () => {
      fetch({ page, per_page: perPage, roles: role, filter });
    },
  });

  useEffect(() => {
    clearAddUserId();
    fetch({ page, per_page: perPage, roles: role, filter });
    return () => {
      clearDelUser();
    };
  }, [role, filter]);

  setLayoutSubheader({
    title: intl.formatMessage(
      {
        id:
          type === 'vendors' || type === 'staff'
            ? 'SUBMENU.VENDOR_BY_ROLE.LIST'
            : 'SUBMENU.BUYER_BY_ROLE.LIST',
      },
      { role: intl.formatMessage({ id: getRoleBreadcrumbName(type as UserListType) }) }
    ),
    breadcrumb: [],
  });
  setLayoutFooter({ show: true });

  if (loading) return <Preloader />;

  return (
    <>
      <Row>
        <Col>
          <Button
            color='primary'
            variant='contained'
            onClick={() => navigate(`/users/new/${type}`)}
            className={classes.addBtn}
          >
            {fm('COMMON.BUTTON.ADD')}
          </Button>

          {(isAdmin || isVendorStaff || isBuyerStaff) &&
            type &&
            (role === 'ROLE_VENDOR_STAFF' || role === 'ROLE_BUYER_STAFF') && (
              <Button
                style={{ marginLeft: '15px' }}
                color='primary'
                variant='contained'
                onClick={() => navigate(`/users/invite/${type}`)}
                className={classes.addBtn}
              >
                {fm('INVITE')}
              </Button>
            )}
          <div className={classes.container}>
            {loading ? (
              <div className={classes.preloaderContainer}>
                <Preloader />
              </div>
            ) : !users.length ? (
              <Typography className={classes.empty} component='h5' variant='h5'>
                {fm('COMMON.LIST.EMPTY')}
              </Typography>
            ) : (
              <Card className={classes.tableContainer}>
                <CardContent className={homeClasses.classes.tableContainer}>
                  <Table aria-label='simple table'>
                    <TableHead>
                      <TableRow>
                        <SHeadTableCell className={classes.rowItems}>
                          {fm('USER.TABLE.ID')}
                        </SHeadTableCell>
                        <SHeadTableCell className={classes.rowItems}>
                          {fm('USER.TABLE.LOGIN')}
                        </SHeadTableCell>
                        <SHeadTableCell className={classes.rowItems}>
                          {fm('USER.TABLE.EMAIL')}
                        </SHeadTableCell>
                        <SHeadTableCell className={classes.rowItems}>
                          {fm('USER.TABLE.FIO')}
                        </SHeadTableCell>
                        <SHeadTableCell className={classes.rowItems}>
                          {fm('USER.TABLE.COMPANY')}
                        </SHeadTableCell>
                        <SHeadTableCell align='right' className={classes.rowItems}>
                          {fm('USER.TABLE.ACTION')}
                        </SHeadTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {users?.map(item => (
                        <TableRow key={item.id}>
                          <TableCell component='th' scope='row' className={classes.rowItems}>
                            {item.id || '-'}
                          </TableCell>
                          <TableCell className={classes.rowItems}>
                            {item.login || '-'}
                          </TableCell>
                          <TableCell className={classes.rowItems}>
                            {item.email || '-'}
                          </TableCell>
                          <TableCell className={classes.rowItems}>{getFio(item)}</TableCell>
                          <TableCell className={classes.rowItems}>
                            {item.company ? (
                              <Link
                                component={RouterLink}
                                to={
                                  isAdmin
                                    ? `/companies/edit/${item.company?.id}`
                                    : `/viewcompany/${item.company?.id}`
                                }
                                className={classes.buttons}
                              >
                                {item.company?.name}
                              </Link>
                            ) : (
                              '-'
                            )}
                          </TableCell>
                          <TableCell align='right'>
                            <div className={classes.actions}>
                              <IconButton
                                color='primary'
                                aria-label='previous'
                                onClick={() => {
                                  clearDelUser();
                                  navigate(`/users/edit/${type}/${item.id}`);
                                }}
                              >
                                <EditIcon />
                              </IconButton>
                              {isAdmin && (
                                <IconButton
                                  color='secondary'
                                  aria-label='previous'
                                  onClick={() => {
                                    setUserId(item.id);
                                    setOpen(true);
                                  }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                    <TableFooter>
                      <TableRow>
                        <TablePaginator
                          page={page}
                          realPerPage={users.length}
                          perPage={perPage}
                          total={total}
                          fetchRows={(data: any) => fetch({ ...data, roles: role, filter })}
                          label={fm('USER.TABLE.PER_PAGE')}
                        />
                      </TableRow>
                    </TableFooter>
                  </Table>
                </CardContent>
              </Card>
            )}
          </div>
        </Col>
      </Row>

      <Modal
        open={open}
        onClose={delUserLoading ? () => {} : () => setOpen(false)}
        title={fm('USER.DELETE.TITLE')}
        loading={delUserLoading}
        content={delUserLoading ? undefined : fm('USER.DELETE.TEXT')}
        actions={
          delUserLoading
            ? undefined
            : [
                {
                  title: fm('COMMON.BUTTON.CANCEL'),
                  onClick: () => setOpen(false),
                },
                {
                  title: fm('COMMON.BUTTON.DELETE'),
                  onClick: () => userId && delUser(userId),
                },
              ]
        }
      />
    </>
  );
};

const connector = connect(
  (state: IAppState) => ({
    userRole: state.auth.user?.roles,
    page: state.users.page,
    perPage: state.users.per_page,
    total: state.users.total,
    users: state.users.users,
    loading: state.users.loading,
    delUserSuccess: state.users.delUserSuccess,
    delUserLoading: state.users.delUserLoading,
    delUserError: state.users.delUserError,
    filter: state.users.filter,
    me: state.profile.me,
  }),
  {
    fetch: usersActions.fetchRequest,
    clearDelUser: usersActions.clearDelUser,
    delUser: usersActions.delUserRequest,
    setUsersList: usersActions.setUsers,
    setFilter: usersActions.setFilter,
    clearAddUserId: usersActions.clearAddUserId,
  }
);

type TPropsFromRedux = ConnectedProps<typeof connector>;

export default connector(UsersPage);
